import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { episodesApi, type CreateEpisodeDto } from '@/services/api/episodesApi';

export const episodeKeys = {
  all: ['episodes'] as const,
  byPatient: (patientId: string) => [...episodeKeys.all, 'by-patient', patientId] as const,
};

export const usePatientEpisodes = (patientId: string | undefined) =>
  useQuery({
    queryKey: episodeKeys.byPatient(patientId ?? ''),
    queryFn: () => episodesApi.byPatient(patientId as string),
    enabled: !!patientId,
    staleTime: 30_000,
    retry: false,
  });

/**
 * Every write invalidates the patient's threads AND the visit list.
 *
 * Attaching a visit changes both sides — the episode's count and the visit's
 * `episodeId` — so refreshing only one leaves the timeline showing a visit on
 * no thread while the card says it has three.
 */
function useEpisodeMutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  patientId: string | undefined,
  success: string,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      if (patientId) qc.invalidateQueries({ queryKey: episodeKeys.byPatient(patientId) });
      qc.invalidateQueries({ queryKey: ['medical-records'] });
      qc.invalidateQueries({ queryKey: ['patient-chart', patientId] });
      toast.success(success);
    },
    onError: (e: Error) => toast.error(e.message || 'That did not go through'),
  });
}

export const useCreateEpisode = (patientId?: string) =>
  useEpisodeMutation((dto: CreateEpisodeDto) => episodesApi.create(dto), patientId, 'Thread opened');

export const useCloseEpisode = (patientId?: string) =>
  useEpisodeMutation(
    ({ id, note }: { id: string; note?: string }) => episodesApi.close(id, note),
    patientId,
    'Thread closed',
  );

export const useReopenEpisode = (patientId?: string) =>
  useEpisodeMutation((id: string) => episodesApi.reopen(id), patientId, 'Thread reopened');

export const useUpdateEpisode = (patientId?: string) =>
  useEpisodeMutation(
    ({ id, ...dto }: { id: string; name?: string; whyOpen?: string; state?: 'open' | 'pending_follow_up' }) =>
      episodesApi.update(id, dto),
    patientId,
    'Thread updated',
  );

export const useAttachVisit = (patientId?: string) =>
  useEpisodeMutation(
    ({ id, medicalRecordId }: { id: string; medicalRecordId: string }) =>
      episodesApi.attach(id, medicalRecordId),
    patientId,
    'Visit added to the thread',
  );
